import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  FileText,
  Video,
  ClipboardList,
  Lightbulb,
  Info,
  Plus,
  Trash2,
  Calendar,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import VideoUrlInput from '../../components/Common/VideoUrlInput';

const ModuleContentBuilder = () => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);

  const [moduleData, setModuleData] = useState({
    title: '',
    description: '',
    duration: 7,
    days: []
  });

  // Initialize 3 days with specific 10-step structure
  const initializeDays = () => {
    return [
      {
        dayNumber: 1,
        title: 'Cognitive Priming & Emotional Engagement',
        description: 'Orientation and Story Episode',
        steps: [
          {
            stepNumber: 0,
            title: 'Skill Orientation',
            type: 'video',
            content: { duration: 2, description: 'Cognitive priming: What, Why, Where.', videoUrl: '' },
            isRequired: true
          },
          {
            stepNumber: 1,
            title: 'Story Episode',
            type: 'video',
            content: { duration: 12, description: 'Emotional engagement: Setup -> Tension -> Decision -> Consequence.', videoUrl: '' },
            isRequired: true
          }
        ]
      },
      {
        dayNumber: 2,
        title: 'Framework & Transferability',
        description: 'SMAART Framework and Domain Scenario',
        steps: [
          {
            stepNumber: 2,
            title: 'SMAART Framework / Tool',
            type: 'video',
            content: { duration: 7, description: 'Transfer story -> reusable mental model.', videoUrl: '' },
            isRequired: true
          },
          {
            stepNumber: 3,
            title: 'Domain Scenario Skin',
            type: 'video',
            content: { duration: 5, description: 'Transferability: Same framework, new context.', videoUrl: '' },
            isRequired: true
          }
        ]
      },
      {
        dayNumber: 3,
        title: 'Micro-Assessment & Application',
        description: 'Assessment, Evidence, Reflection, and Conclusion',
        steps: [
          {
            stepNumber: 4,
            title: 'Micro-Assessment',
            type: 'quiz',
            content: {
              description: 'Part A: Scenario MCQ, Part B: Decision Trade-Off',
              questions: []
            },
            isRequired: true
          },
          {
            stepNumber: 5,
            title: 'Evidence Task (Artefact)',
            type: 'submission',
            content: {
              description: 'Proof of application: Decision note, Checklist, etc.',
              instructions: '',
              rubric: ''
            },
            isRequired: true
          },
          {
            stepNumber: 6,
            title: 'Reflection (NVQ-Style)',
            type: 'reflection',
            content: {
              description: 'Learning transfer: Likert statements & forced-choice prompts.',
              questions: []
            },
            isRequired: true
          },
          {
            stepNumber: 7,
            title: 'Founder Congratulatory Video',
            type: 'video',
            content: { duration: 3, description: 'Motivation + identity building.', videoUrl: '' },
            isRequired: true
          },
          {
            stepNumber: 8,
            title: 'Flash Card + Interview Prep',
            type: 'flashcard',
            content: { description: 'One-page reference.', fileUrl: '' },
            isRequired: true
          },
          {
            stepNumber: 9,
            title: 'Post-Employment Application Trigger',
            type: 'reflection',
            content: {
              description: 'Unlocked after internship/job. Questions: Where applied? Outcome?',
              questions: []
            },
            isRequired: false
          }
        ]
      }
    ];
  };

  const fetchModuleData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await api.get(`/courses/${courseId}/modules/${moduleId}`);
      if (data.success && data.data) {
        const module = data.data;
        const hasSavedDays = Array.isArray(module.days) && module.days.length > 0;

        setModuleData({
          title: module.title || '',
          description: module.description || '',
          duration: module.duration || 3,
          days: hasSavedDays ? module.days : initializeDays()
        });

        setExpandedDays({ 1: true });
        setSelectedDay(1);
      }
    } catch (error) {
      console.error('Failed to fetch module:', error);
      toast.error('Failed to load module data');
      setModuleData({
        title: '',
        description: '',
        duration: 3,
        days: initializeDays()
      });
      setExpandedDays({ 1: true });
      setSelectedDay(1);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [courseId, moduleId]);

  useEffect(() => {
    setLoading(true);
    fetchModuleData();
  }, [fetchModuleData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/courses/${courseId}/modules/${moduleId}`, {
        title: moduleData.title,
        description: moduleData.description,
        duration: moduleData.duration,
        days: moduleData.days,
        timeAllocation: moduleData.duration * 24 * 60 // Estimate
      });
      if (data.success) {
        toast.success('Module content saved successfully');
        await fetchModuleData(true);
      } else {
        toast.error(data.message || 'Failed to save module content');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save module content');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (dayNumber) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayNumber]: !prev[dayNumber]
    }));
    setSelectedDay(dayNumber);
  };

  const updateDay = (dayNumber, field, value) => {
    setModuleData(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.dayNumber === dayNumber
          ? { ...day, [field]: value }
          : day
      )
    }));
  };

  const updateDayTitle = (dayNumber, title) => {
    setModuleData(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.dayNumber === dayNumber
          ? { ...day, title }
          : day
      )
    }));
  };



  const updateStepContent = (dayNumber, stepIndex, field, value) => {
    setModuleData(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.dayNumber === dayNumber
          ? {
            ...day,
            steps: day.steps.map((step, idx) =>
              idx === stepIndex
                ? { ...step, content: { ...step.content, [field]: value } }
                : step
            )
          }
          : day
      )
    }));
  };

  // Helper to add questions to quiz/reflection steps
  const addQuestion = (dayNumber, stepIndex) => {
    setModuleData(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.dayNumber === dayNumber
          ? {
            ...day,
            steps: day.steps.map((step, idx) =>
              idx === stepIndex
                ? {
                  ...step,
                  content: {
                    ...step.content,
                    questions: [...(step.content.questions || []), { question: '', options: ['', '', '', ''], correctAnswer: '', type: 'mcq', points: 10 }]
                  }
                }
                : step
            )
          }
          : day
      )
    }));
  };

  // Helper to update questions
  const updateQuestion = (dayNumber, stepIndex, qIndex, field, value) => {
    setModuleData(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.dayNumber === dayNumber
          ? {
            ...day,
            steps: day.steps.map((step, idx) =>
              idx === stepIndex
                ? {
                  ...step,
                  content: {
                    ...step.content,
                    questions: step.content.questions.map((q, qi) =>
                      qi === qIndex ? { ...q, [field]: value } : q
                    )
                  }
                }
                : step
            )
          }
          : day
      )
    }));
  };

  const removeQuestion = (dayNumber, stepIndex, qIndex) => {
    setModuleData(prev => ({
      ...prev,
      days: prev.days.map(day =>
        day.dayNumber === dayNumber
          ? {
            ...day,
            steps: day.steps.map((step, idx) =>
              idx === stepIndex
                ? {
                  ...step,
                  content: {
                    ...step.content,
                    questions: step.content.questions.filter((_, qi) => qi !== qIndex)
                  }
                }
                : step
            )
          }
          : day
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const currentDay = moduleData.days.find(d => d.dayNumber === selectedDay);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/courses/${courseId}/builder`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff]">{moduleData.title || 'Module Content Builder'}</h1>
              <p className="text-sm text-gray-500 mt-1">Build content for the 3-day skill structure</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-md hover:shadow-lg transition-all font-semibold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Days Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
                Module Days
              </h3>
              <div className="space-y-2">
                {moduleData.days.map(day => (
                  <button
                    key={day.dayNumber}
                    onClick={() => toggleDay(day.dayNumber)}
                    className={`w-full text-left p-3 rounded-lg mb-2 transition-all ${selectedDay === day.dayNumber
                      ? 'bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-md'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold">Day {day.dayNumber}</span>
                      {expandedDays[day.dayNumber] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    <div className={`text-xs truncate ${selectedDay === day.dayNumber ? 'text-blue-100' : 'text-gray-500'}`}>
                      {day.title || 'Untitled Day'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {currentDay && (
              <div className="space-y-6">
                {/* Day Header & Metadata */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                      <Info className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Day {currentDay.dayNumber} Details</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Day Title</label>
                      <input
                        type="text"
                        value={currentDay.title || ''}
                        onChange={(e) => updateDayTitle(currentDay.dayNumber, e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., Cognitive Priming"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={currentDay.description || ''}
                        onChange={(e) => updateDay(currentDay.dayNumber, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Brief description of the day's goals"
                      />
                    </div>
                  </div>
                </div>

                {/* Steps Loop */}
                {currentDay.steps && currentDay.steps.map((step, idx) => (
                  <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${step.type === 'video' ? 'bg-red-100 text-red-600' :
                          step.type === 'quiz' ? 'bg-purple-100 text-purple-600' :
                            step.type === 'reflection' ? 'bg-amber-100 text-amber-600' :
                              step.type === 'submission' ? 'bg-green-100 text-green-600' :
                                'bg-blue-100 text-blue-600'
                          }`}>
                          {step.type === 'video' ? <Video className="w-5 h-5" /> :
                            step.type === 'quiz' ? <ClipboardList className="w-5 h-5" /> :
                              step.type === 'reflection' ? <Lightbulb className="w-5 h-5" /> :
                                step.type === 'submission' ? <FileText className="w-5 h-5" /> :
                                  <ClipboardList className="w-5 h-5" />}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Step {step.stepNumber}: {step.title}
                          </h3>
                          <span className="text-xs uppercase tracking-wide font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {step.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Common Fields */}
                      <p className="text-sm text-gray-600 italic bg-gray-50 p-3 rounded border border-gray-100">
                        {step.content?.description || 'No description provided'}
                      </p>

                      {/* Video Type Editor */}
                      {step.type === 'video' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <VideoUrlInput
                              label="Video URL"
                              value={step.content?.videoUrl || ''}
                              onChange={(v) => updateStepContent(currentDay.dayNumber, idx, 'videoUrl', v)}
                              focusRingClass="focus:ring-red-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Duration (mins)</label>
                            <input
                              type="number"
                              value={step.content?.duration || 0}
                              onChange={(e) => updateStepContent(currentDay.dayNumber, idx, 'duration', parseInt(e.target.value))}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                        </div>
                      )}

                      {/* Quiz / Reflection Type Editor */}
                      {(step.type === 'quiz' || step.type === 'reflection') && (
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">Questions</label>
                              <button
                                onClick={() => addQuestion(currentDay.dayNumber, idx)}
                                className="flex items-center px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add Question
                              </button>
                            </div>
                            <div className="space-y-3">
                              {step.content?.questions?.map((q, qIdx) => (
                                <div key={qIdx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                  <div className="flex justify-between mb-2">
                                    <span className="text-sm font-medium">Q{qIdx + 1}</span>
                                    <button
                                      onClick={() => removeQuestion(currentDay.dayNumber, idx, qIdx)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={q.question || ''}
                                    onChange={(e) => updateQuestion(currentDay.dayNumber, idx, qIdx, 'question', e.target.value)}
                                    className="w-full px-3 py-2 border rounded mb-2 text-sm"
                                    placeholder="Question text"
                                  />
                                  {step.type === 'quiz' && (
                                    <div className="space-y-2 pl-4 border-l-2 border-indigo-200">
                                      <p className="text-xs text-gray-500 mb-1 font-medium">Select the correct answer option:</p>
                                      {q.options?.map((opt, oIdx) => (
                                        <div key={oIdx} className="flex items-center space-x-2">
                                          <input
                                            type="radio"
                                            name={`day-${currentDay.dayNumber}-step-${idx}-q-${qIdx}-correct`}
                                            checked={q.correctAnswer === opt && opt !== ''}
                                            onChange={() => {
                                              updateQuestion(currentDay.dayNumber, idx, qIdx, 'correctAnswer', opt);
                                            }}
                                            className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                            title="Mark as correct answer"
                                          />
                                          <input
                                            type="text"
                                            value={opt || ''}
                                            onChange={(e) => {
                                              const newOptions = [...q.options];
                                              newOptions[oIdx] = e.target.value;
                                              updateQuestion(currentDay.dayNumber, idx, qIdx, 'options', newOptions);
                                              // Auto-update correctAnswer if we're editing the correct option
                                              if (q.correctAnswer === opt) {
                                                updateQuestion(currentDay.dayNumber, idx, qIdx, 'correctAnswer', e.target.value);
                                              }
                                            }}
                                            className={`w-full px-3 py-1.5 border rounded text-xs transition-colors ${q.correctAnswer === opt && opt !== '' ? 'border-green-500 bg-green-50/50' : ''}`}
                                            placeholder={`Option ${oIdx + 1}`}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {(!step.content?.questions || step.content?.questions.length === 0) && (
                                <p className="text-sm text-gray-500 italic">No questions added yet.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Submission / Evidence Type Editor */}
                      {step.type === 'submission' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
                            <textarea
                              value={step.content?.instructions || ''}
                              onChange={(e) => updateStepContent(currentDay.dayNumber, idx, 'instructions', e.target.value)}
                              rows={4}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              placeholder="Detailed instructions for the evidence task..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Rubric / Evaluation Criteria</label>
                            <textarea
                              value={step.content?.rubric || ''}
                              onChange={(e) => updateStepContent(currentDay.dayNumber, idx, 'rubric', e.target.value)}
                              rows={3}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              placeholder="How will this be scored?"
                            />
                          </div>
                        </div>
                      )}

                      {/* Flashcard Type Editor */}
                      {step.type === 'flashcard' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Resource URL / File</label>
                            <input
                              type="text"
                              value={step.content?.fileUrl || ''}
                              onChange={(e) => updateStepContent(currentDay.dayNumber, idx, 'fileUrl', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Link to flashcard image or document"
                            />
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleContentBuilder;
